import { useEffect, useState } from 'react';
import { Preloader } from 'konsta/react';
import { Check, CheckCircle2, Copy, Gift, MessageCircleQuestion, Send, Star, Ticket, X } from 'lucide-react';
import { CANCEL_REASONS } from '@harmony/shared';
import { api, type FeedbackPrompt, type OnboardingItem, type Promotion, type Referrals } from '../api';
import { Avatar, BottomSheet, EmptyState, PrimaryButton, TopBar, useToast } from '../components/ui';
import { inputClass } from '../components/form';
import { haptic, tg } from '../telegram';
import { useErrorText, useLang, useT } from '../i18n';

function openShare(url: string, text: string) {
  const share = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  if (tg?.openTelegramLink) tg.openTelegramLink(share);
  else window.open(share, '_blank');
}

// ─────────────── Invite friends ───────────────

export function InvitePage() {
  const t = useT();
  const [r, setR] = useState<Referrals | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    api.referrals().then(setR).catch(() => undefined);
  }, []);
  return (
    <div className="flex h-full flex-col">
      <TopBar title={t('invite.title')} />
      {!r ? (
        <div className="flex justify-center pt-24">
          <Preloader />
        </div>
      ) : !r.enabled ? (
        <EmptyState icon="🎁" text={t('invite.off')} />
      ) : (
        <div className="flex-1 overflow-y-auto px-4 pb-10 pt-6">
          <div className="flex flex-col items-center text-center">
            <div className="mb-3 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-pink-500 text-white shadow-lg">
              <Gift size={38} />
            </div>
            <h2 className="mb-2 text-2xl font-bold">{t('invite.title')}</h2>
            <p className="mb-5 text-tg-hint">{t('invite.body', { event: t(`invite.event.${r.event}`), credits: r.rewardCredits })}</p>
          </div>
          <div className="mb-4 grid grid-cols-2 gap-2 text-center">
            <div className="rounded-2xl bg-tg-secondary p-3">
              <div className="text-2xl font-bold">{r.joined}</div>
              <div className="text-xs text-tg-hint">{t('invite.joined')}</div>
            </div>
            <div className="rounded-2xl bg-tg-secondary p-3">
              <div className="text-2xl font-bold text-green-700">+{r.creditsEarned}</div>
              <div className="text-xs text-tg-hint">{t('invite.earned')}</div>
            </div>
          </div>
          <button
            className="mb-3 flex w-full items-center gap-2 rounded-2xl bg-tg-secondary px-4 py-3 text-left"
            onClick={async () => {
              await navigator.clipboard?.writeText(r.link).catch(() => undefined);
              haptic('success');
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            <span className="min-w-0 flex-1 truncate text-tg-link">{copied ? t('agent.copied') : r.link}</span>
            {copied ? <Check size={18} className="text-green-600" /> : <Copy size={18} className="text-tg-hint" />}
          </button>
          <PrimaryButton
            onClick={() => {
              haptic();
              openShare(r.link, t('invite.share_text'));
            }}
          >
            <Send size={18} /> {t('invite.share')}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
}

// ─────────────── Help ───────────────

export function HelpPage() {
  const t = useT();
  const [h, setH] = useState<{ faq: string; announcement: string; supportUsername: string | null; botLink: string } | null>(null);
  useEffect(() => {
    api.help().then(setH).catch(() => undefined);
  }, []);
  const openSupport = () => {
    const link = h?.supportUsername ? `https://t.me/${h.supportUsername}` : h?.botLink;
    if (!link) return;
    if (tg?.openTelegramLink) tg.openTelegramLink(link);
    else window.open(link, '_blank');
  };
  // FAQ is written as "Q: … / A: …" blocks separated by blank lines.
  const qa = (h?.faq ?? '')
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean)
    .map((b) => {
      const lines = b.split('\n');
      return { q: lines[0].replace(/^(Q|ጥ)\s*[:፦]\s*/, ''), a: lines.slice(1).join('\n').replace(/^(A|መ)\s*[:፦]\s*/, '') };
    });
  return (
    <div className="flex h-full flex-col">
      <TopBar title={t('profile.help')} />
      <div className="flex-1 overflow-y-auto px-4 pb-10 pt-4">
        {h?.announcement && <div className="mb-4 whitespace-pre-wrap rounded-2xl bg-amber-400/15 p-4 text-[15px]">📣 {h.announcement}</div>}
        <div className="mb-5 rounded-2xl bg-tg-secondary p-4">
          <p className="mb-3 text-[15px]">{t('help.page_body')}</p>
          <PrimaryButton onClick={openSupport}>
            <MessageCircleQuestion size={18} /> {t('help.open_bot')}
          </PrimaryButton>
        </div>
        {!!qa.length && (
          <>
            <h2 className="mb-2 text-sm font-semibold text-tg-hint">{t('help.faq')}</h2>
            <div className="space-y-2">
              {qa.map((x, i) => (
                <details key={i} className="rounded-2xl bg-tg-secondary px-4 py-3">
                  <summary className="cursor-pointer font-medium">{x.q}</summary>
                  <p className="mt-2 whitespace-pre-wrap text-[15px] text-tg-hint">{x.a}</p>
                </details>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────── Welcome tutorial & checklist ───────────────

/** A few swipe-through cards shown once, the first time someone opens the app after registering. */
export function TutorialSheet() {
  const t = useT();
  const [items, setItems] = useState<OnboardingItem[]>([]);
  const [i, setI] = useState(0);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api
      .onboarding()
      .then((r) => {
        if (r.tutorial.length) {
          setItems(r.tutorial);
          setOpen(true);
        }
      })
      .catch(() => undefined);
  }, []);
  const close = () => {
    setOpen(false);
    api.tutorialSeen().catch(() => undefined);
  };
  if (!items.length) return null;
  const item = items[i];
  const last = i === items.length - 1;
  const emoji = ['👋', '💆', '💬', '⭐', '🎉'][i] ?? '✨';
  return (
    <BottomSheet open={open} onClose={close}>
      <div className="flex flex-col items-center px-2 pb-2 pt-3 text-center">
        <div className="mb-4 text-6xl">{emoji}</div>
        <h3 className="mb-2 text-xl font-bold">{item.title}</h3>
        {item.body && <p className="mb-5 text-[16px] text-tg-hint">{item.body}</p>}
        <div className="mb-5 flex gap-1.5">
          {items.map((_, j) => (
            <span key={j} className={`h-2 rounded-full transition-all ${j === i ? 'w-6 bg-tg-button' : 'w-2 bg-black/15'}`} />
          ))}
        </div>
        <PrimaryButton
          onClick={() => {
            haptic('select');
            if (last) close();
            else setI(i + 1);
          }}
        >
          {last ? t('tut.done') : t('tut.next')}
        </PrimaryButton>
        {!last && (
          <button onClick={close} className="mt-3 text-sm text-tg-hint">
            {t('tut.skip')}
          </button>
        )}
      </div>
    </BottomSheet>
  );
}

/** "Getting started" steps that tick themselves as the user does them. Hidden when all are done. */
export function ChecklistCard() {
  const t = useT();
  const [items, setItems] = useState<OnboardingItem[]>([]);
  useEffect(() => {
    api.onboarding().then((r) => setItems(r.checklist)).catch(() => undefined);
  }, []);
  if (!items.length) return null;
  const done = items.filter((x) => x.done).length;
  return (
    <div className="mx-4 mt-3 rounded-2xl bg-tg-secondary p-4">
      <div className="mb-1 flex items-center justify-between">
        <span className="font-semibold">{t('checklist.title')}</span>
        <span className="text-sm text-tg-hint">{t('checklist.progress', { done, total: items.length })}</span>
      </div>
      <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-black/10">
        <div className="h-full rounded-full bg-green-500" style={{ width: `${(done / items.length) * 100}%` }} />
      </div>
      <div className="space-y-2">
        {items.map((x) => (
          <div key={x.id} className="flex items-start gap-2.5">
            <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${x.done ? 'bg-green-500 text-white' : 'border-2 border-black/20'}`}>
              {x.done && <Check size={13} strokeWidth={3} />}
            </span>
            <span className={x.done ? 'text-tg-hint line-through' : ''}>
              {x.title}
              {!x.done && x.body && <span className="block text-sm text-tg-hint">{x.body}</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────── Promotions ───────────────

export function PromoBanner() {
  const t = useT();
  const lang = useLang();
  const toast = useToast();
  const [items, setItems] = useState<Promotion[]>([]);
  const [hidden, setHidden] = useState<string[]>([]);
  useEffect(() => {
    api.promotions().then(setItems).catch(() => undefined);
  }, []);
  const shown = items.filter((p) => !hidden.includes(p.id));
  if (!shown.length) return null;
  const p = shown[0];
  return (
    <div className="mx-3 mt-2 flex items-center gap-3 rounded-2xl bg-[#F0A93B] p-3 text-[#1E1B3A] shadow">
      <Ticket size={26} className="shrink-0" />
      <button
        className="min-w-0 flex-1 text-left"
        onClick={async () => {
          await navigator.clipboard?.writeText(p.code).catch(() => undefined);
          haptic('success');
          toast(`${t('agent.copied')}: ${p.code}`);
        }}
      >
        <div className="font-semibold leading-tight">{lang === 'am' ? p.bannerAm : p.bannerEn}</div>
        <div className="text-sm opacity-90">{t('promo.use_code', { code: p.code })}</div>
      </button>
      <button onClick={() => setHidden((h) => [...h, p.id])} className="shrink-0 rounded-full p-1 active:bg-white/20" aria-label="close">
        <X size={18} />
      </button>
    </div>
  );
}

// ─────────────── "Did you meet?" ───────────────

/** Shows waiting feedback questions as cards (the same question also arrives in the bot). */
export function FeedbackCards() {
  const [items, setItems] = useState<FeedbackPrompt[]>([]);
  useEffect(() => {
    api.feedbackPending().then(setItems).catch(() => undefined);
  }, []);
  if (!items.length) return null;
  return (
    <div className="space-y-2 px-3 pt-2">
      {items.map((p) => (
        <FeedbackCard key={p.id} p={p} onDone={() => setItems((x) => x.filter((i) => i.id !== p.id))} />
      ))}
    </div>
  );
}

function FeedbackCard({ p, onDone }: { p: FeedbackPrompt; onDone: () => void }) {
  const t = useT();
  const errText = useErrorText();
  const toast = useToast();
  const [mode, setMode] = useState<'ask' | 'rate' | 'cancel' | 'done'>('ask');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [thanks, setThanks] = useState('');

  const answer = async (body: Parameters<typeof api.answerFeedback>[1], msg: string) => {
    setBusy(true);
    try {
      await api.answerFeedback(p.id, body);
      haptic('success');
      setThanks(msg);
      setMode('done');
      setTimeout(onDone, 1800);
    } catch (e) {
      toast(errText(e));
      if ((e as { status?: number }).status === 409) onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-tg-bg p-4 shadow-sm ring-1 ring-black/5">
      <div className="mb-3 flex items-center gap-3">
        <Avatar src={p.provider.photo} name={p.provider.name} size={44} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold">{t('fb.card_title', { name: p.provider.name })}</div>
          <div className="text-sm text-tg-hint">{t('provider.min', { minutes: p.minutes })}</div>
        </div>
      </div>
      {mode === 'ask' && (
        <div className="grid gap-2">
          <PrimaryButton disabled={busy} onClick={() => setMode('rate')}>
            {t('fb.met')}
          </PrimaryButton>
          <div className="grid grid-cols-2 gap-2">
            <PrimaryButton tone="soft" disabled={busy} onClick={() => answer({ outcome: 'NOT_MET_YET' }, t('fb.ask_later'))}>
              {t('fb.not_yet')}
            </PrimaryButton>
            <PrimaryButton tone="soft" disabled={busy} onClick={() => setMode('cancel')}>
              {t('fb.canceled')}
            </PrimaryButton>
          </div>
        </div>
      )}
      {mode === 'rate' && (
        <div>
          <p className="mb-2 text-center text-tg-hint">{t('fb.rate', { name: p.provider.name })}</p>
          <div className="mb-3 flex justify-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => {
                  haptic('select');
                  setRating(n);
                }}
                className="p-1 active:scale-90"
                aria-label={`${n}`}
              >
                <Star size={36} className={n <= rating ? 'fill-amber-400 text-amber-400' : 'text-black/20'} />
              </button>
            ))}
          </div>
          <input className={inputClass} maxLength={300} placeholder={t('fb.comment')} value={comment} onChange={(e) => setComment(e.target.value)} />
          <PrimaryButton
            className="mt-3"
            disabled={!rating || busy}
            onClick={() => answer({ outcome: 'MET', rating, comment: comment.trim() || undefined }, t('fb.thanks'))}
          >
            {t('fb.send')}
          </PrimaryButton>
        </div>
      )}
      {mode === 'cancel' && (
        <div>
          <p className="mb-2 text-tg-hint">{t('fb.cancel_reason')}</p>
          <div className="flex flex-wrap gap-2">
            {CANCEL_REASONS.map((r) => (
              <button
                key={r}
                disabled={busy}
                onClick={() => answer({ outcome: 'CANCELED', reason: r }, t('fb.canceled_ok'))}
                className="rounded-full bg-tg-secondary px-3.5 py-2 text-sm active:opacity-70"
              >
                {t(`cancel.${r}`)}
              </button>
            ))}
          </div>
        </div>
      )}
      {mode === 'done' && (
        <p className="flex items-center justify-center gap-2 py-2 font-medium text-green-700">
          <CheckCircle2 size={20} /> {thanks}
        </p>
      )}
    </div>
  );
}
